import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Key, Mail, Search, Download, Shield, Crown,
  Copy, Check, Calendar, Globe, RefreshCw, ChevronRight, Package
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function LicenseCard({ purchase, onDownload, downloadingKey }) {
  const [copied, setCopied] = useState(false);
  const isExpired = purchase.license_expiry && purchase.license_expiry < new Date().toISOString();
  const isRefunded = purchase.refunded;
  const isActive = !isExpired && !isRefunded;

  const copyKey = () => {
    navigator.clipboard.writeText(purchase.license_key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const planLabels = {
    starter: "Starter (1 Website)",
    business: "Business (3 Websites)",
    agency: "Agency (7 Websites)",
    enterprise: "Enterprise (Unlimited)",
  };

  return (
    <div
      className={`border rounded-xl p-5 transition-all ${
        isActive
          ? "border-[#067d06]/30 bg-[#067d06]/[0.02]"
          : "border-[#E4E4E7] bg-[#F9FAFB] opacity-75"
      }`}
      data-testid={`license-card-${purchase.license_key?.slice(-5)}`}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-bold px-2 py-0.5 rounded ${
              isActive
                ? "bg-[#e7f7e7] text-[#067d06]"
                : isRefunded
                ? "bg-[#fee2e2] text-[#b12704]"
                : "bg-[#fef3cd] text-[#856404]"
            }`}>
              {isActive ? "Active" : isRefunded ? "Refunded" : "Expired"}
            </span>
            <span className="text-xs text-[#A1A1AA] capitalize">
              {planLabels[purchase.plan_id] || purchase.plan_id}
            </span>
          </div>
          <div className="text-lg font-bold text-[#09090B]">${purchase.amount}/year</div>
        </div>
        <div className="text-right text-xs text-[#A1A1AA]">
          <div>Purchased {purchase.created_at?.slice(0, 10)}</div>
          <div>Expires {purchase.license_expiry?.slice(0, 10)}</div>
        </div>
      </div>

      {/* License Key */}
      <div className="bg-white border border-[#E4E4E7] rounded-lg p-3 mb-4">
        <div className="flex items-center gap-2 mb-1.5">
          <Key size={12} className="text-[#FF7300]" />
          <span className="text-[10px] font-bold text-[#A1A1AA] uppercase tracking-wider">License Key</span>
        </div>
        <div className="flex items-center gap-2">
          <code className="flex-1 text-sm font-mono font-bold text-[#09090B] tracking-wide break-all" data-testid="portal-license-key">
            {purchase.license_key}
          </code>
          <button
            onClick={copyKey}
            className="flex items-center gap-1 px-2.5 py-1.5 bg-[#F4F4F5] hover:bg-[#E4E4E7] text-[#52525B] text-xs font-medium rounded transition-colors cursor-pointer border-0 flex-shrink-0"
            data-testid="portal-copy-key-btn"
          >
            {copied ? <Check size={12} className="text-[#067d06]" /> : <Copy size={12} />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      </div>

      {/* Actions */}
      {isActive && (
        <div className="flex gap-2">
          <button
            onClick={() => onDownload(purchase.license_key)}
            disabled={downloadingKey === purchase.license_key}
            className="flex-1 flex items-center justify-center gap-2 bg-[#FF7300] text-white rounded-md py-2.5 text-sm font-bold hover:bg-[#E66800] transition-colors cursor-pointer border-0 disabled:opacity-50"
            data-testid="portal-download-btn"
          >
            {downloadingKey === purchase.license_key ? (
              <><RefreshCw size={14} className="animate-spin" /> Preparing...</>
            ) : (
              <><Download size={14} /> Download PRO Plugin</>
            )}
          </button>
        </div>
      )}

      {isRefunded && (
        <p className="text-xs text-[#b12704] mt-2">
          This license has been refunded and is no longer active.
        </p>
      )}

      {isExpired && !isRefunded && (
        <div className="text-center mt-2">
          <p className="text-xs text-[#856404] mb-2">Your license has expired.</p>
          <Link
            to="/#pricing"
            className="text-xs font-bold text-[#FF7300] hover:underline"
          >
            Renew License
          </Link>
        </div>
      )}
    </div>
  );
}

export default function CustomerPortal() {
  const [lookupType, setLookupType] = useState("email");
  const [lookupValue, setLookupValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [downloadingKey, setDownloadingKey] = useState("");

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!lookupValue.trim()) return;
    setLoading(true);
    setError("");
    setData(null);

    try {
      const body = lookupType === "email"
        ? { email: lookupValue.trim() }
        : { license_key: lookupValue.trim() };

      const res = await fetch(`${API}/portal/lookup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (res.status === 404) {
        setError("No purchases found. Check your email or license key and try again.");
        return;
      }
      if (!res.ok) throw new Error("Lookup failed");

      const result = await res.json();
      setData(result);
    } catch (e) {
      setError(e.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (licenseKey) => {
    setDownloadingKey(licenseKey);
    try {
      const res = await fetch(`${API}/portal/download`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          license_key: licenseKey,
          version: "pro",
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Download failed");
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "amazon-affiliate-pro.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      // Refresh data to show updated download history
      if (data) {
        const body = lookupType === "email"
          ? { email: lookupValue.trim() }
          : { license_key: lookupValue.trim() };
        const refRes = await fetch(`${API}/portal/lookup`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (refRes.ok) setData(await refRes.json());
      }
    } catch (e) {
      alert(e.message || "Download failed");
    } finally {
      setDownloadingKey("");
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="customer-portal">
      {/* Header */}
      <header className="bg-white border-b border-[#E4E4E7] px-6 py-4 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <Link to="/" className="text-[#A1A1AA] hover:text-[#09090B] transition-colors">
            <ArrowLeft size={20} />
          </Link>
          <h1 className="font-display font-bold text-[#09090B] text-lg">My Account</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        {/* Lookup Form */}
        {!data && (
          <div className="animate-fade-in-up">
            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#FF7300]/10 mb-4">
                <Crown size={28} className="text-[#FF7300]" />
              </div>
              <h2 className="font-display font-extrabold text-[#09090B] text-2xl sm:text-3xl tracking-tight mb-2">
                Customer Portal
              </h2>
              <p className="text-[#52525B] text-sm max-w-md mx-auto">
                Access your license keys, download your plugin, and manage your subscription.
              </p>
            </div>

            <div className="bg-white border border-[#E4E4E7] rounded-xl p-6 max-w-md mx-auto" data-testid="portal-lookup-form">
              {/* Toggle */}
              <div className="flex gap-1 bg-[#F4F4F5] rounded-lg p-1 mb-5">
                <button
                  onClick={() => setLookupType("email")}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-xs font-semibold rounded-md transition-all cursor-pointer border-0 ${
                    lookupType === "email"
                      ? "bg-white text-[#09090B] shadow-sm"
                      : "text-[#71717a] hover:text-[#09090B]"
                  }`}
                  data-testid="lookup-by-email-btn"
                >
                  <Mail size={14} /> By Email
                </button>
                <button
                  onClick={() => setLookupType("license")}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-xs font-semibold rounded-md transition-all cursor-pointer border-0 ${
                    lookupType === "license"
                      ? "bg-white text-[#09090B] shadow-sm"
                      : "text-[#71717a] hover:text-[#09090B]"
                  }`}
                  data-testid="lookup-by-license-btn"
                >
                  <Key size={14} /> By License Key
                </button>
              </div>

              <form onSubmit={handleLookup}>
                <label className="block text-xs font-bold text-[#09090B] mb-1.5">
                  {lookupType === "email" ? "PayPal Email Address" : "License Key"}
                </label>
                <input
                  type={lookupType === "email" ? "email" : "text"}
                  value={lookupValue}
                  onChange={(e) => setLookupValue(e.target.value)}
                  placeholder={lookupType === "email" ? "you@example.com" : "AFFPRO-XXXXX-XXXXX-XXXXX-XXXXX"}
                  className="w-full border border-[#E4E4E7] rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF7300]/30 focus:border-[#FF7300] mb-4"
                  required
                  data-testid="portal-lookup-input"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 bg-[#09090B] text-white rounded-md py-2.5 text-sm font-bold hover:bg-[#27272A] transition-colors cursor-pointer border-0 disabled:opacity-50"
                  data-testid="portal-lookup-submit"
                >
                  {loading ? (
                    <><RefreshCw size={14} className="animate-spin" /> Looking up...</>
                  ) : (
                    <><Search size={14} /> Find My Purchase</>
                  )}
                </button>
              </form>

              {error && (
                <div className="mt-4 bg-[#fee2e2] border border-[#b12704]/20 rounded-lg p-3 text-center" data-testid="portal-error">
                  <p className="text-xs text-[#b12704]">{error}</p>
                </div>
              )}
            </div>

            <p className="text-center text-xs text-[#A1A1AA] mt-6">
              Need help? Contact <a href="mailto:info@amznawp.com" className="text-[#FF7300] font-semibold hover:underline">info@amznawp.com</a>
            </p>
          </div>
        )}

        {/* Results */}
        {data && (
          <div className="animate-fade-in-up">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-display font-bold text-[#09090B] text-xl">Your Licenses</h2>
                <p className="text-xs text-[#52525B]">{data.purchases.length} purchase{data.purchases.length !== 1 ? "s" : ""} found</p>
              </div>
              <button
                onClick={() => { setData(null); setLookupValue(""); }}
                className="text-xs font-medium text-[#52525B] hover:text-[#09090B] flex items-center gap-1 cursor-pointer bg-transparent border-0"
                data-testid="portal-back-btn"
              >
                <ArrowLeft size={12} /> Back
              </button>
            </div>

            {/* License Cards */}
            <div className="space-y-4 mb-8">
              {data.purchases.map((p, i) => (
                <LicenseCard
                  key={i}
                  purchase={p}
                  onDownload={handleDownload}
                  downloadingKey={downloadingKey}
                />
              ))}
            </div>

            {/* Download History */}
            {data.downloads.length > 0 && (
              <div className="border border-[#E4E4E7] rounded-xl bg-white overflow-hidden" data-testid="download-history">
                <div className="p-4 border-b border-[#E4E4E7]">
                  <h3 className="font-display font-bold text-[#09090B] text-sm flex items-center gap-2">
                    <Download size={14} className="text-[#FF7300]" /> Download History
                  </h3>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#F9FAFB] text-left text-[#A1A1AA] text-xs uppercase tracking-wider">
                      <th className="px-4 py-2.5">Date</th>
                      <th className="px-4 py-2.5">Version</th>
                      <th className="px-4 py-2.5">Plan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.downloads.map((d, i) => (
                      <tr key={i} className="border-t border-[#F4F4F5]">
                        <td className="px-4 py-2.5 text-[#52525B]">{d.downloaded_at?.slice(0, 16).replace("T", " ")}</td>
                        <td className="px-4 py-2.5">
                          <span className="text-xs font-bold px-2 py-0.5 bg-[#FF7300]/10 text-[#FF7300] rounded">PRO</span>
                        </td>
                        <td className="px-4 py-2.5 capitalize text-[#09090B] font-medium">{d.plan_id}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Help Section */}
            <div className="mt-8 bg-white border border-[#E4E4E7] rounded-xl p-5">
              <h3 className="font-display font-bold text-[#09090B] text-sm mb-3">Quick Links</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Link
                  to="/#pricing"
                  className="flex items-center gap-2 text-sm text-[#52525B] hover:text-[#FF7300] transition-colors no-underline"
                >
                  <Package size={14} /> Upgrade Plan
                </Link>
                <Link
                  to="/refund-policy"
                  className="flex items-center gap-2 text-sm text-[#52525B] hover:text-[#FF7300] transition-colors no-underline"
                >
                  <Shield size={14} /> Refund Policy
                </Link>
                <a
                  href="mailto:info@amznawp.com"
                  className="flex items-center gap-2 text-sm text-[#52525B] hover:text-[#FF7300] transition-colors no-underline"
                >
                  <Mail size={14} /> Contact Support
                </a>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
